import type { ProductEntity } from '@/entities/assortment'
import type { FieldCellProps } from '@/entities/column'
import type { LocalStateUpdater } from '@/shared/components/data-grid/helpers'
import type {
	SRT_Row,
	SRT_TableInstance,
} from '@/shared/components/master-table'
import type {
	BrandModel,
	FieldSchemaModel,
	OptimizationModel,
	PricingCampaignModel,
	ProductsQueryItemModel,
} from '@/shared/generated'

import { Fragment, useCallback, useMemo } from 'react'
import { useIsFetching } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { prop, uniqBy } from 'lodash/fp'
import { Maximize2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	getEntityFieldValue,
	getNodeId,
	getPricingTypeChoices,
	getRowId,
} from '@/entities/assortment'
import {
	createChoices,
	createColumnProcessor,
	useColumnsFields,
} from '@/entities/column'
import { useBrandsQuery, useCategoriesQuery } from '@/entities/core'
import {
	FINAL_FIELDS,
	getOgStatus,
	getStatusBadgeVariant,
	useOptimizationsQuery,
} from '@/entities/optimization'
import {
	useOptimizationGroupsQuery,
	useSalesEntitiesQuery,
} from '@/entities/optimization-group'
import {
	getAlertAnnotationDataChoices,
	usePricingAlertsAnnotationsQuery,
} from '@/entities/price-alert'
import {
	getPcBadgeVariant,
	getPricingCampaignEngine,
	usePricingCampaignsQuery,
} from '@/entities/pricing-campaign'

import { useTableChangesStorage } from '@/shared/components/data-grid/helpers'
import { createSRTColumnHelper } from '@/shared/components/master-table'
import { Button } from '@/shared/components/ui/button'
import { LinkButton } from '@/shared/components/ui/link-button'
import {
	Modal,
	ModalBody,
	ModalContent,
	ModalHeader,
	ModalTitle,
	ModalTrigger,
} from '@/shared/components/ui/modal'
import { Spinner } from '@/shared/components/ui/spinner'
import { FieldType, OptimizationStatus } from '@/shared/generated'
import { useStore } from '@/shared/lib/datasheet/store'

export function useColumns(schema: FieldSchemaModel[]) {
	const { t } = useTranslation()

	const { data: sales_entity } = useSalesEntitiesQuery()
	const { data: category } = useCategoriesQuery({})
	const { data: brand } = useBrandsQuery(
		{},
		{
			select: uniqBy<BrandModel>('id'),
		}
	)
	const { data: optimization_group } = useOptimizationGroupsQuery()
	const { data: pricing_campaign } = usePricingCampaignsQuery()
	const { data: optimization } = useOptimizationsQuery({
		select: (data) =>
			data.map((optimization) => ({
				...optimization,
				id: optimization.optimization_group_id,
			})),
	})
	const { data: alert } = usePricingAlertsAnnotationsQuery({
		select: (data) => getAlertAnnotationDataChoices(data, t),
	})
	const entities = useMemo(
		() => ({
			sales_level: (sales_entity?.sales_levels || []).map((e) => ({
				...e,
				id: e.level,
			})),
			sales_entity: sales_entity?.sales_entities || [],
			category: category || [],
			brand: brand || [],
			optimization_group: optimization_group || [],
			pricing_campaign: pricing_campaign || [],
			optimization: optimization || [],
			alert: alert || [],
		}),
		[
			sales_entity,
			category,
			brand,
			optimization_group,
			pricing_campaign,
			alert,
			optimization,
		]
	)
	const getChoices = useCallback(
		(schema: FieldSchemaModel[]) => {
			// Formtted data choices to use in data grid

			const getChoiceExtraParams = <T,>(field: FieldSchemaModel, choice: T) => {
				if (field.reference?.entity === 'pricing_campaign') {
					return {
						variant: getPcBadgeVariant(
							getPricingCampaignEngine(choice as PricingCampaignModel)
						),
					}
				}
				if (field.reference?.entity === 'optimization') {
					const status = getOgStatus((choice as OptimizationModel).status)

					return {
						label: t(`opt_${status}`, { defaultValue: status }),
						status,
						variant: getStatusBadgeVariant(status),
					}
				}

				return {}
			}

			return {
				pricing_type: getPricingTypeChoices(t),
				...createChoices({
					entities,
					schema,
					getChoiceExtraParams,
				}),
			}
		},
		[entities, t]
	)
	const transformedSchema = useMemo(
		() =>
			schema.map((field) => {
				if (field.id === 'pos_ids') {
					return {
						...field,
						field_type: FieldType.Chip,
					}
				}

				return field
			}),
		[schema]
	)
	const ogProductCustomLocalStateUpdater: LocalStateUpdater<
		ProductsQueryItemModel | ProductEntity,
		any
	> = ({ control, name, defaultValue, value, row }) => {
		const isPricingLine = Boolean((row as ProductsQueryItemModel).products)
		const products = (row as ProductsQueryItemModel).products
		const newState = { ...control.localData } as Record<string, any>
		const updateState = (id: string) => {
			if (value == defaultValue) {
				if (newState[id]) {
					delete newState[id][name]
				}
				if (newState[id] && Object.keys(newState[id]).length === 0) {
					delete newState[id]
				}
			} else {
				newState[id] = {
					...(newState[id] || {}),
					[name]: value,
				}
			}
		}
		if (isPricingLine) {
			// Set value for all products in line if it is a pricing line
			updateState(getRowId(row as ProductEntity))
			products.forEach((product) => {
				const productId = `${getNodeId(row)}__${product.sku}`
				updateState(productId)
			})
		} else {
			// Set value for all products in line if it is not a pricing line
			updateState(`${getNodeId(row)}__`)
			updateState(getRowId(row as ProductEntity))
		}

		return newState
	}

	const setLocalData = useStore((state) => state.setLocalData)
	const localData = useStore((state) => state.localData)
	const tableChangesStorage = useTableChangesStorage({
		setLocalData,
		localData,
		getId: getRowId,
		updater: ogProductCustomLocalStateUpdater,
	})

	return useColumnsFields<ProductsQueryItemModel>({
		schema: transformedSchema,
		getChoices,
		processor: createColumnProcessor<ProductsQueryItemModel>(
			{
				columnHelper,
				/**
				 * Check if the cell is editable based on the optimization group status and the field id is in FINAL_FIELDS
				 */
				enableCellEditing: ({ row, table, cell }) => {
					const statusColumn = table.getColumn('optimization_group_status')
					const status = prop(
						[
							'columnDef',
							'meta',
							'choices',
							row.original.optimization_group_id,
							'status',
						],
						statusColumn
					)
					if (FINAL_FIELDS.indexOf(cell.column.id) === -1) {
						return t('disabled_reason.assortment_final_fields', {
							defaultValue:
								'Editing of this field is not allowed on this page, please use the optimization group page.',
						})
					} else if (
						status === OptimizationStatus.New ||
						status === OptimizationStatus.Locked
					) {
						return t('disabled_reason.assortment_optimization_wrong_status', {
							defaultValue:
								'This field is not editable because the optimization group is in "New" or "Locked" status.',
						})
					}

					return true
				},
				tableChangesStorage,
				getCell(schema) {
					if (schema.reference?.entity === 'optimization_group') {
						return OptimizationGroupCell
					}
					if (schema.id === 'sku') {
						return ProductIdentField
					}

					return null
				},
				getAccessorFn: (schema: FieldSchemaModel) => {
					switch (schema.id) {
						case 'sku':
							return (row: ProductsQueryItemModel | ProductEntity) =>
								(row as ProductEntity).sku ??
								(row as ProductsQueryItemModel).line_id
						case 'title':
							return (row: ProductsQueryItemModel | ProductEntity) =>
								(row as ProductEntity).title ??
								(row as ProductsQueryItemModel)?.fields?.name
						default:
							return (row) => getEntityFieldValue(row, schema.id)
					}
				},
			},
			t
		),
	})
}

const columnHelper = createSRTColumnHelper<ProductsQueryItemModel>()
const OptimizationGroupCell = (props: FieldCellProps<FieldType.Chip>) => {
	const { t } = useTranslation()

	const isFetching = useIsFetching({ queryKey: ['optimization-groups'] })
	if (isFetching) {
		return (
			<div className='flex items-center gap-2 opacity-50'>
				<Spinner className='size-4' />
				<span>{t('loading', { defaultValue: 'Loading...' })}</span>
			</div>
		)
	}
	if (props.value === null || props.value === undefined) {
		return <div>-</div>
	}

	return (
		<LinkButton asChild>
			<Link
				to='/p/og/$optimizationGroupId/products'
				params={{ optimizationGroupId: props.value }}
			>
				{props.choices?.[props.value]?.label}
			</Link>
		</LinkButton>
	)
}
function ProductIdentField({
	value,
	table,
	tableRow,
}: {
	value: string
	table: SRT_TableInstance<ProductsQueryItemModel>
	tableRow: SRT_Row<ProductsQueryItemModel>
}) {
	const setIsSkuPanelOpen = useStore((state) => state.setIsSidebarOpen)
	const setSkuSidebarContent = useStore((state) => state.setSidebarContent)

	const onClick = () => {
		setIsSkuPanelOpen(true)
		setSkuSidebarContent(tableRow.original)
	}

	return (
		<div className='group relative flex max-w-full items-center justify-between gap-2'>
			<LinkButton variant='brand' onClick={onClick}>
				{value}
			</LinkButton>
			<Modal>
				<ModalTrigger asChild>
					<Button
						variant='secondary'
						size='icon-xs'
						className='absolute top-0 right-0 opacity-0 group-hover:opacity-100'
					>
						<Maximize2Icon />
					</Button>
				</ModalTrigger>
				<ModalContent className='min-w-5xl'>
					<ProductIdentModal title={value} table={table} row={tableRow} />
				</ModalContent>
			</Modal>
		</div>
	)
}
const ProductIdentModal = ({
	table,
	row,
	title,
}: {
	table: SRT_TableInstance<ProductsQueryItemModel>
	row: SRT_Row<ProductsQueryItemModel>
	title: string
}) => {
	const fields = row.getAllCells()

	return (
		<>
			<ModalHeader>
				<ModalTitle>{title}</ModalTitle>
			</ModalHeader>
			<ModalBody className='max-h-[80vh] overflow-y-auto'>
				<dl className='grid grid-cols-1 text-base/6 sm:grid-cols-[min(50%,--spacing(80))_auto] sm:text-sm/6'>
					{fields
						.filter(
							(field) =>
								['sku', 'srt-row-expand', 'srt-row-select'].indexOf(
									field.column.id
								) === -1
						)
						.map((field) => (
							<Fragment key={field.column.id}>
								<dt className='col-start-1 border-t pt-3 text-muted-foreground first:border-none sm:border-t sm:py-3'>
									{field.column.columnDef.header}
								</dt>
								<dd className='flex justify-start pt-1 pb-3 sm:border-t sm:py-3 sm:nth-2:border-none [&>div]:w-auto!'>
									{field.column.columnDef.Cell?.({
										cell: field,
										column: field.column,
										renderedCellValue: field.renderValue() as any,
										cellValue: field.getValue() as any,
										row,
										table,
									})}
								</dd>
							</Fragment>
						))}
				</dl>
			</ModalBody>
		</>
	)
}
